import React, { useCallback, useRef } from 'react';
import { defineMessages, FormattedNumber, useIntl } from 'react-intl';
import { StyleSheet, type StyleProp, View, type ViewStyle } from 'react-native';
import { Pressable, type PressableStateCallbackType } from 'react-native-gesture-handler';

import {
  BodyText,
  CloudinaryImage,
  type CloudinaryTransformConfig,
  QuantityStepperV2,
  theme,
  useHovered,
  useQuantityStepperV2,
  useResponsive,
  webOnlyStyles,
} from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Upsell product option component.
 *
 * @note This component is memoized because the parent re-renders due to tracking open stepper state.
 */
export const BagUpsellProductOption = React.memo((props: BagUpsellProductOptionProps) => {
  const {
    product,
    size,
    isAddingToCart,
    isDisabled = false,
    addUpsellToBag,
    updateOpenSteppers,
  } = props;

  const { name, imageUrl, cost, channelCost } = product;

  const stepperButtonSize = STEPPER_BUTTON_SIZE_BY_SIZE[size];

  const { match } = useResponsive();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  // ─── Refs ────────────────────────────────────────────────────────────

  const cardRef = useRef(null);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isHovered = useHovered(cardRef);

  const imageContainerStyles = [
    styles.imageContainer,
    imageContainerWebStyles,
    isHovered && !isDisabled ? styles.imageContainerHovered : undefined,
  ];
  const imageStyles = [
    styles.image,
    imageWebStyles,
    isHovered && !isDisabled ? styles.imageHovered : undefined,
  ];

  const handleAddUpsell = useCallback(
    (quantity: number) => {
      addUpsellToBag({ quantity, product });
    },
    [addUpsellToBag, product],
  );

  // ─── Stepper Logic ─────────────────────────────────────────────────────────

  const onShowStepper = useCallback(() => {
    updateOpenSteppers(product.id, true);
  }, [product.id, updateOpenSteppers]);

  const onHideStepper = useCallback(() => {
    updateOpenSteppers(product.id, false);
  }, [product.id, updateOpenSteppers]);

  const { quantity, isStepperShown, increaseQuantity, decreaseQuantity, showStepper } =
    useQuantityStepperV2({
      onChange: handleAddUpsell,
      shouldResetOnTimeout: true,
      onShowStepper,
      onHideStepper,
      onIncrease: onShowStepper,
      onDecrease: onShowStepper,
    });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleCardPress = useCallback(() => {
    if (isDisabled) return;

    track('bag_upsell.product_tapped_outside_stepper');
    showStepper();
  }, [isDisabled, track, showStepper]);

  const getCardStyles = useCallback(
    (state: PressableStateCallbackType): StyleProp<ViewStyle> => {
      const { pressed } = state;

      return [
        styles.card,
        containerStyles[size],
        cardWebStyles,
        isDisabled ? disabledCardWebStyles : undefined,
        pressed && !isDisabled ? styles.cardPressed : undefined,
      ];
    },
    [isDisabled, size],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View>
      <Pressable
        ref={cardRef}
        accessibilityRole="button"
        accessibilityLabel={name}
        disabled={isDisabled}
        onPress={handleCardPress}
        style={getCardStyles}
      >
        <View style={imageContainerStyles}>
          <CloudinaryImage
            baseUrl={imageUrl}
            config={CLOUDINARY_IMG_CONFIG[size]}
            contentFit="contain"
            style={imageStyles}
            aria-label={name}
          />
        </View>

        <View style={styles.textContainer}>
          <BodyText sizeMatch={['14']} numberOfLines={size === 'sm' ? 3 : 2}>
            {name}
          </BodyText>

          <BodyText sizeMatch={['14']}>
            <FormattedNumber value={(channelCost ?? cost) / 100} style="currency" currency="USD" />
          </BodyText>
        </View>
      </Pressable>

      <View style={styles.stepperContainer} pointerEvents="box-none">
        <View
          style={[
            styles.stepperInnerContainer,
            match([styles.stepperInnerContainerXS, styles.stepperInnerContainerSM]),
          ]}
          pointerEvents="box-none"
        >
          <QuantityStepperV2.Container>
            <QuantityStepperV2.ToggleButton
              accessibilityLabel={formatMessage(messages.addToBagA11yLabel, {
                product_name: name,
              })}
              isLoading={isAddingToCart}
              isDisabled={isDisabled}
              onPress={showStepper}
              size={stepperButtonSize}
            />

            <QuantityStepperV2.FloatingContainer isShown={isStepperShown} size="sm">
              <QuantityStepperV2.DecreaseButton
                onPress={decreaseQuantity}
                accessibilityLabel={formatMessage(messages.decreaseQuantity, {
                  product_name: name,
                })}
                isDisabled={isDisabled}
                size={stepperButtonSize}
                palette="transparent"
              />

              {isAddingToCart ? null : <QuantityStepperV2.Quantity quantity={quantity} size="sm" />}

              <QuantityStepperV2.IncreaseButton
                onPress={increaseQuantity}
                accessibilityLabel={formatMessage(messages.increaseQuantity, {
                  product_name: name,
                })}
                isDisabled={isDisabled}
                palette="transparent"
                size={stepperButtonSize}
              />
            </QuantityStepperV2.FloatingContainer>
          </QuantityStepperV2.Container>
        </View>
      </View>
    </View>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CARD_WIDTH = {
  sm: 90,
  md: 100,
  lg: 173,
} as const;

const CLOUDINARY_IMG_CONFIG: Record<Size, CloudinaryTransformConfig> = {
  sm: [{ crop: 'crop', width: 0.4, height: 0.56 }, { width: CARD_WIDTH.md }], // NOTE: On purpose to reduce calls to cloudinary
  md: [{ crop: 'crop', width: 0.4, height: 0.56 }, { width: CARD_WIDTH.md }],
  lg: [{ crop: 'crop', width: 0.4, height: 0.56 }, { width: CARD_WIDTH.lg }],
};

const STEPPER_BUTTON_SIZE_BY_SIZE: Record<Size, 'xs' | 'sm'> = {
  sm: 'xs',
  md: 'xs',
  lg: 'sm',
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addToBagA11yLabel: {
    defaultMessage: 'Add {product_name} to bag',
    description: 'Upsell Rail | Product | Add to bag',
  },
  increaseQuantity: {
    defaultMessage: 'Increase quantity of "{product_name}"',
    description: 'Upsell Rail | Product | Increase quantity of',
  },
  decreaseQuantity: {
    defaultMessage: 'Decrease quantity of "{product_name}"',
    description: 'Upsell Rail | Product | Decrease quantity of',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const containerStyles = StyleSheet.create({
  sm: {
    width: CARD_WIDTH.sm,
  },
  md: {
    width: CARD_WIDTH.md,
  },
  lg: {
    width: CARD_WIDTH.lg,
  },
});

const styles = StyleSheet.create({
  card: {
    flexGrow: 1,
    gap: theme.spacing['2'],
  },
  cardPressed: {
    opacity: 0.7,
  },
  imageContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
    borderRadius: theme.radius.large,
    padding: theme.spacing['4'],
    overflow: 'hidden',
  },
  imageContainerHovered: {
    opacity: 0.85,
  },
  image: {
    width: '100%',
    aspectRatio: 1,
  },
  imageHovered: {
    transform: [{ scale: 1.04 }],
  },
  textContainer: {
    gap: theme.spacing['2'],
    justifyContent: 'space-between',
    flexGrow: 1,
  },
  stepperContainer: {
    ...StyleSheet.absoluteFill,
  },
  stepperInnerContainer: {
    aspectRatio: 1,
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    padding: theme.spacing['2'],
  },
  stepperInnerContainerXS: {
    padding: theme.spacing['1'],
  },
  stepperInnerContainerSM: {
    padding: theme.spacing['2'],
  },
});

const cardWebStyles = webOnlyStyles({
  cursor: 'pointer',
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
});

const disabledCardWebStyles = webOnlyStyles({
  cursor: 'auto',
});

const imageContainerWebStyles = webOnlyStyles({
  transition: `opacity ${theme.transitions.base}ms`,
});

const imageWebStyles = webOnlyStyles({
  transition: `transform ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BagUpsellProductOptionProps = {
  product: ProductUpsell;
  size: Size;
  addUpsellToBag: (params: AddUpsellToBagParams) => void;
  isAddingToCart: boolean;
  isDisabled?: boolean;
  updateOpenSteppers: (productId: string, isStepperShown: boolean) => void;
};

type Size = 'sm' | 'md' | 'lg';

type ProductUpsell = {
  id: string;
  name: string;
  cost: number;
  channelCost?: number;
  imageUrl: string;
};

type AddUpsellToBagParams = {
  quantity: number;
  product: ProductUpsell;
};
